import { useMemo, useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import cn from "classnames";
import { FCX } from "@datassential/platform-ui-lib";
import { IFoodProfileSummaryData } from "@models/NewFoodProfileSummary";
import Chart from "@pages/Food/components/SummaryChart/SummaryChart";
import ChartTabs from "@pages/Food/components/ChartTabs/ChartTabs";
import MacStageMenu from "@pages/Food/components/MacStageMenu/MacStageMenu";
import { buildPenetrationChartData, pctValueFormatter } from "@pages/Food/Food.helpers";
import "./FoodMenuPenetration.scss";

type Props = Pick<IFoodProfileSummaryData, 'slices' | 'macStage' | 'suggestedMacKeywords' | 'categoryName'>;

/** Menu penetration for the past years plus the Menucast prediction, filtered by restaurant segment. */
const FoodMenuPenetration: FCX<Props> = ({
    slices,
    macStage,
    suggestedMacKeywords,
    categoryName,
    className,
}) => {
    const [selectedSliceIndex, setSelectedSliceIndex] = useState(0);
    // Falls back to the first segment if new data has fewer segments than the one selected.
    const activeSliceIndex = selectedSliceIndex < slices.length ? selectedSliceIndex : 0;

    const chartData = useMemo(
        () => buildPenetrationChartData(slices),
        [slices]
    );

    const tabs = useMemo(
        () => slices.map(slice => ({
            id: slice.id,
            name: `${slice.name} ${pctValueFormatter(slice.penetration)}`,
        })),
        [slices]
    );

    if (chartData === null) return null;

    return (
        <div className={cn("FoodMenuPenetration", className)} data-testid="blockFoodMenuPenetration">
            <div className="FoodMenuPenetration__header">
                <MacStageMenu
                    macStage={macStage}
                    suggestedMacKeywords={suggestedMacKeywords}
                    categoryName={categoryName}
                />
            </div>
            <div className="FoodMenuPenetration__chart">
                <AutoSizer>
                    {({ width, height }) => (
                        <Chart
                            width={width}
                            height={height}
                            {...chartData}
                            activeChartIndex={activeSliceIndex}
                            chartsTitles={slices.map(s => s.name)}
                            printValue={(v) => `${v.toFixed(1)}%`}
                        />
                    )}
                </AutoSizer>
            </div>
            <div className="FoodMenuPenetration__footer">
                <ChartTabs
                    data={tabs}
                    activeItemIndex={activeSliceIndex}
                    setActiveItemIndex={setSelectedSliceIndex}
                />
            </div>
        </div>
    );
};

export default FoodMenuPenetration;
